import { WorkspaceMembership, WorkspaceContextType } from '../types/authTypes';

import { oidcUserManagerSettings } from '@config/authConfig';

interface RawRole {
  code?: string;
  Code?: string;
  displayName?: string;
}

interface RawMembership {
  membershipId?: string;
  MembershipId?: string;
  tenantId?: string;
  TenantId?: string;
  tenantName?: string;
  TenantName?: string;
  contextType?: string;
  ContextType?: string;
  companyId?: string;
  CompanyId?: string;
  companyName?: string;
  CompanyName?: string;
  roles?: RawRole[] | string[];
  Roles?: RawRole[] | string[];
}

function normalizeRoles(raw: RawRole[] | string[] | undefined): string[] {
  if (!raw || raw.length === 0) return [];
  if (typeof raw[0] === 'string') return raw as string[];
  return (raw as RawRole[])
    .map((r) => r.code ?? r.Code ?? r.displayName ?? '')
    .filter(Boolean);
}

function normalizeMembership(raw: RawMembership): WorkspaceMembership {
  return {
    membershipId: raw.membershipId ?? raw.MembershipId ?? '',
    tenantId: raw.tenantId ?? raw.TenantId ?? '',
    tenantName: raw.tenantName ?? raw.TenantName ?? '',
    contextType: (raw.contextType ??
      raw.ContextType ??
      'company') as WorkspaceContextType,
    companyId: raw.companyId ?? raw.CompanyId,
    companyName: raw.companyName ?? raw.CompanyName,
    roles: normalizeRoles(raw.roles ?? raw.Roles),
  };
}

export async function fetchWorkspaceMemberships(
  accessToken: string,
  signal?: AbortSignal
): Promise<WorkspaceMembership[]> {
  const authority = (oidcUserManagerSettings.authority ?? '').replace(
    /\/$/,
    ''
  );

  const response = await fetch(`${authority}/api/v1/memberships`, {
    headers: { Authorization: `Bearer ${accessToken}` },
    signal,
  });

  if (!response.ok) {
    throw new Error(
      `Failed to fetch workspace memberships (${response.status})`
    );
  }

  const body = await response.json();
  // API returns either a top-level array or a wrapped { memberships: [] } object
  const raw: RawMembership[] = Array.isArray(body)
    ? body
    : body.memberships ?? body.Memberships ?? [];
  return raw.map(normalizeMembership);
}
